import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, map, catchError, BehaviorSubject, of, Subject, takeUntil } from 'rxjs';
import {
  ProviderSearchRequest,
  FacilitySearchRequest,
  KEngineResponseContainerProviderResponseOfMasteredLocations,
  ProviderResponseOfMasteredLocations,
  FacilityResponseOfMasteredLocations,
  KEngineResponseContainerFacilityResponseOfMasteredLocations,
  ProviderResponseOfPractitionerRoles,
  FacilityResponseOfOrgAffiliations,
  BasicProviderSearchQuery,
  BasicOrganizationSearchQuery,
  KEngineResponseContainerFacilityResponseOfOrgAffiliations,
  KEngineResponseContainerProviderResponseOfPractitionerRoles,
} from '../apiV2/models';
import { ProviderSearchService, FacilitySearchService } from '../apiV2/services';
import { CURRENT_TENNANT } from 'src/app/app.component';
import * as Handlebars from 'handlebars';
import { CareFindrTenantConfigurationService } from "../apiV2/services/care-findr-tenant-configuration.service";


@Injectable({
  providedIn: 'root',
})
export class SearchV2Service implements OnDestroy{
  constructor(
    private http: HttpClient,
    private providerSearchService: ProviderSearchService,
    private facilitySearchService: FacilitySearchService,
    private tenantConfigServiceV2: CareFindrTenantConfigurationService,
  ) {}

  private _providerSearchResults =
    new BehaviorSubject<ProviderResponseOfPractitionerRoles>({});
  private _facilitySearchResults =
    new BehaviorSubject<FacilityResponseOfOrgAffiliations>({});
  private _providerPrintResults =
    new BehaviorSubject<ProviderResponseOfPractitionerRoles>({});
  private _facilityPrintResults =
    new BehaviorSubject<FacilityResponseOfOrgAffiliations>({});

  private cancelProviderRequest$ = new Subject<void>();
  private cancelFacilityRequest$ = new Subject<void>();

  providerSearchResults$: Observable<ProviderResponseOfPractitionerRoles> =
    this._providerSearchResults.asObservable();
  facilitySearchResults$: Observable<FacilityResponseOfOrgAffiliations> =
    this._facilitySearchResults.asObservable();
  providerPrintResults$: Observable<ProviderResponseOfPractitionerRoles> =
    this._providerPrintResults.asObservable();
  facilityPrintResults$: Observable<FacilityResponseOfOrgAffiliations> =
    this._facilityPrintResults.asObservable();

  getProviderResults(
    queryParams: BasicProviderSearchQuery,
    print: boolean = false
  ): Observable<ProviderResponseOfPractitionerRoles> {

    const requestBody: ProviderSearchRequest = {
      queryType: 'mtProviderSearchV2',
      query: queryParams,
    };

    if(print) {
      this._providerPrintResults.next({});
      this.cancelProviderRequest$.next();
    }

    return this.providerSearchService
      .multiTenantProviderSearchV2CareFindr({
        'x-tenant-id': CURRENT_TENNANT,
        'x-persona': 'user',
        'Accept-Language': localStorage.getItem('translateLanguageTo') || 'en',
        body: requestBody,
      })
      .pipe(
        this.takeProviderHelper(print),
        map((response: any) => {
          const dataResponse = response.data;
          if (dataResponse?.total && dataResponse.providers) {
            print ? this._providerPrintResults.next(dataResponse) :
                  this._providerSearchResults.next(dataResponse)
            return dataResponse;
          }
          console.log(
            dataResponse ? 'No Results Found.' : 'Response Undefined'
          );
          const emptyResponse: ProviderResponseOfPractitionerRoles = {providers: [], meta: {}, total: 0};
          print ? this._providerPrintResults.next(emptyResponse) :
                  this._providerSearchResults.next(emptyResponse)
          return emptyResponse;
        }),
        catchError((e: HttpErrorResponse) => {
          console.log(e);
          const emptyResponse: ProviderResponseOfPractitionerRoles = {providers: [], meta: {}, total: 0};
          print ? this._providerPrintResults.next(emptyResponse) :
                  this._providerSearchResults.next(emptyResponse)
          return of(emptyResponse);
        })
      );
  }

  getFacilityResults(
    queryParams: BasicOrganizationSearchQuery,
    print: boolean = false
  ): Observable<FacilityResponseOfOrgAffiliations> {
    const requestBody: FacilitySearchRequest = {
      queryType: 'mtOrganizationSearchV2',
      query: queryParams,
    };

    if(print) {
      this._facilityPrintResults.next({});
      this.cancelFacilityRequest$.next();
    }

    return this.facilitySearchService
      .multiTenantFacilitySearchV2CareFindr({
        'x-tenant-id': CURRENT_TENNANT,
        'x-persona': 'user',
        'Accept-Language': localStorage.getItem('translateLanguageTo') || 'en',
        body: requestBody,
      })
      .pipe(
        this.takeFacilityHelper(print),
        map((response: any) => {
          const dataResponse = response.data;
          if (dataResponse?.total && dataResponse.facilities) {
            print ? this._facilityPrintResults.next(dataResponse) :
                    this._facilitySearchResults.next(dataResponse);
            return dataResponse;
          }
          console.log(
            dataResponse ? 'No Results Found.' : 'Response Undefined'
          );
          const emptyResponse: FacilityResponseOfOrgAffiliations = {facilities: [], meta: {}, total: 0};
          print ? this._facilityPrintResults.next(emptyResponse) :
                  this._facilitySearchResults.next(emptyResponse);
          return emptyResponse;
        }),
        catchError((e: HttpErrorResponse) => {
          console.log(e);
          const emptyResponse: FacilityResponseOfOrgAffiliations = {facilities: [], meta: {}, total: 0};
          print ? this._facilityPrintResults.next(emptyResponse) :
                  this._facilitySearchResults.next(emptyResponse);
          return of(emptyResponse);
        })
      );
  }

  getProviderDetails(
    stableId: string,
    lat?: string,
    lon?: string,
    locationStableIdHash?: string
  ): Observable<ProviderResponseOfMasteredLocations | undefined> {
    const params: any = {
      'x-tenant-id': CURRENT_TENNANT,
      'x-persona': 'user',
      'Accept-Language': localStorage.getItem('translateLanguageTo') || 'en',
      uid: stableId,
      'location-stable-id-hash': locationStableIdHash
    };
    if (lat !== undefined) { params.lat = lat; }
    if (lon !== undefined) { params.lng = lon; }

    return this.providerSearchService
      .multiTenantProviderDetailCareFindr(params)
      .pipe(
        map(
          (
            response: KEngineResponseContainerProviderResponseOfMasteredLocations
          ) => {
            return response.data;
          }
        ),
        catchError((e: HttpErrorResponse) => {
          console.log(e);
          return of(undefined);
        })
      );
  }

  getFacilityDetails(
    stableId: string
  ): Observable<FacilityResponseOfMasteredLocations | undefined> {
    return this.facilitySearchService
      .multiTenantFacilityDetailCareFindr({
        'x-tenant-id': CURRENT_TENNANT,
        'x-persona': 'user',
        'Accept-Language': localStorage.getItem('translateLanguageTo') || 'en',
        uid: stableId,
      })
      .pipe(
        map(
          (
            response: KEngineResponseContainerFacilityResponseOfMasteredLocations
          ) => {
            return response.data;
          }
        ),
        catchError((e: HttpErrorResponse) => {
          console.log(e);
          return of(undefined);
        })
      );
  }

  // Method to get current provider results
  getCurrentProviderResults(): ProviderResponseOfPractitionerRoles {
    return this._providerSearchResults.getValue();
  }

  // Method to get current facility results
  getCurrentFacilityResults(): FacilityResponseOfOrgAffiliations {
    return this._facilitySearchResults.getValue();
  }

  takeProviderHelper(print: boolean) {
    return (source: Observable<KEngineResponseContainerProviderResponseOfPractitionerRoles>):               Observable<KEngineResponseContainerProviderResponseOfPractitionerRoles> => {
      if(print) {
        return source.pipe(takeUntil(this.cancelProviderRequest$))
        }
      return source
    }
  }

  takeFacilityHelper(print: boolean) {
    return (source: Observable<KEngineResponseContainerFacilityResponseOfOrgAffiliations>):
      Observable<KEngineResponseContainerFacilityResponseOfOrgAffiliations> => {
        if(print) {
          return source.pipe(takeUntil(this.cancelFacilityRequest$))
        }
        return source
      }
  }

  // getCareFindrJson(tenant: string, pageName: string): Observable<any> {
  //   let requestOptions: Object = {responseType: "text"}
  //   const url = `${this.baseUrl}/component-blueprint/${pageName}`;
  //   const headers = new HttpHeaders({
  //     'tenant': tenant
  //   });
  //
  //   return this.http.get<any>(url, { headers });
  // }

  // //To load the search-start.hbs file
  getCareFindrJson(url: any): Observable<any> {
    let requestOptions: Object = {responseType: "text"}

    return this.http.get<any>(url, requestOptions);
  }

  registerPartial(name: string, url: string):Promise<void>{
    let requestOptions: Object = {responseType: "text"}
    return this.http.get<any>(url, requestOptions).toPromise().then((response: any) =>{
      Handlebars.registerPartial(name, Handlebars.compile(response));
    });
  }
  fetchPartialFromDB(file_name: string, partial_name: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.tenantConfigServiceV2.getTenantComponentBluePrint({ page_name: file_name, tenant: CURRENT_TENNANT })
        .subscribe({
          next: (response) => {
            if (response.encodedTemplate == null) {
              return resolve(); // No-op if nothing to load
            }

            const res = atob(response.encodedTemplate);
            // Ensure the name is passed explicitly
            Handlebars.registerPartial(partial_name, Handlebars.compile(res));
            resolve();
          },
          error: (err) => {
            reject(err);
          }
        });
    });
  }

  ngOnDestroy(): void {
    this.cancelProviderRequest$.next()
    this.cancelProviderRequest$.complete()
    this.cancelFacilityRequest$.next()
    this.cancelFacilityRequest$.complete();
  }
}
